/**
 * "File shares (optional)" of the Azure Storage form (plan §3.2 «Named shares»). An Entra identity that holds only a
 * Storage File Data role can read the files inside a share but cannot list the account's shares (listing needs a
 * role that can read the storage account, for example Reader), so the connection names the shares it opens directly.
 * Shown for Entra sign-in at account scope with File ticked; comma- or newline-separated; each entry is checked with
 * the share-name rule. The field is not dial-relevant: a connected connection's edit replaces the set in place
 * (`namedShareActions.replaceNamedShares`) instead of reconnecting. A form field, not an action: it fires no event.
 */
import { useEffect, useState } from 'react';
import { shareNameProblem } from '@/core/engines/azureStorageFilePaths';
import type { ConnectionFormValues } from './connectionFormSchema';
import { namedSharesFromText } from './azureStorageNamedShares';
import { NAMED_SHARES_INPUT_TEST_ID } from './storageLegRemedy';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { TEXTAREA_CLASS, type ConnectionFormApi } from '../connection-dialog/fields/fieldControls';

export function AzureStorageNamedSharesField({ form, values }: { form: ConnectionFormApi; values: Partial<ConnectionFormValues> }) {
  const persisted = values.azureStorageNamedShares ?? [];
  const [text, setText] = useState(() => persisted.join('\n'));
  // A value written from outside the field (a loaded record, an answer the form persisted) replaces the draft text.
  useEffect(() => {
    if (namedSharesFromText(text).join('\n') !== persisted.join('\n')) setText(persisted.join('\n'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persisted.join('\n')]);
  const problems = namedSharesFromText(text).flatMap((name) => {
    const problem = shareNameProblem(name);
    return problem ? [`${name}: ${problem}`] : [];
  });
  return (
    <FormRow row="azureStorageNamedShares" label="File shares" htmlFor="storage-named-shares-input" note="optional" testId="storage-named-shares">
      <textarea
        id="storage-named-shares-input"
        rows={2}
        className={TEXTAREA_CLASS}
        placeholder="share-one, share-two"
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          const names = namedSharesFromText(event.target.value);
          form.setFieldValue('azureStorageNamedShares', names.length > 0 ? names : undefined);
        }}
        data-testid={NAMED_SHARES_INPUT_TEST_ID}
      />
      {problems.map((problem) => (
        <p key={problem} className="mt-1 text-xs text-destructive" data-testid="storage-named-shares-problem">{problem}</p>
      ))}
      <p className="mt-1 text-xs text-muted-foreground">
        With Microsoft Entra sign-in, listing file shares needs a role that can read the storage account (for example
        Reader). Storage File Data roles cover only the files inside a share, so name the shares to open them directly.
      </p>
    </FormRow>
  );
}
